import * as React from 'react';
import Investor, { Account, AccountId, KYCStatus } from '../../shared/Investor';
import { FieldTypes, PropertyBag, PropertyBagProps } from '../../shared/KYCPolicy';
import { getInvestor, getKYCPolicy, updateInvestorKYCInformation } from '../../globals/ApiHooks'
import { DynamicPropertyInput, SelectInput } from '../../components/atoms/Inputs'
import { lang } from '../../globals/Translations';
import { TranslatedTerms } from '../../globals/TranslatedTerms';
import { IsMobile, useWindowSize } from '../../globals/UIHooks';
import Stepper from '../atoms/Stepper';
import PopupMessage from '../molecules/PopupMessage'
import { CreateAccountInputs } from '../molecules/CreateAccountInputs';
import { DefaultPersonalAccountId } from '../../shared/Globals';


type Props = {
  showPageInformation?: boolean,
  controllActiveStep?: (state: number) => void,
  controllSelectedAccount?: (state: AccountId) => void,
  preSelectedAccountIndex?: number,
  loadingCompleted?: () => void
}

const AccountsToStringArray = (accounts: Account[]) => accounts.map((i) => i.accountName)

export const EditInvestor: React.FC<Props> = ({ controllActiveStep, showPageInformation, controllSelectedAccount, preSelectedAccountIndex, loadingCompleted }) => {
  const [policy, setPolicy] = React.useState<PropertyBag>()
  const [investor, setInvestor] = React.useState<Investor>()
  const [selectedAccountIndex, setSelectedAccountIndex] = React.useState(preSelectedAccountIndex || 0)
  const [activeStepIndex, setActiveStepIndex] = React.useState(0)

  const account = investor && investor.accounts && investor.accounts[selectedAccountIndex]
  const accounts = [...(investor && AccountsToStringArray(investor.accounts) || []), lang(TranslatedTerms.CREATE_NEW_ACCOUNT)]

  const [showCreateAccount, setShowCreateAccount] = React.useState(false)

  const [loading, setLoadingLocal] = React.useState(false)
  const [updatedProperties, setUpdatedProperties] = React.useState<PropertyBagProps | undefined>(undefined)
  const { width } = useWindowSize()

  const [message, setMessage] = React.useState<string | undefined>()

  React.useEffect(() => {
    console.log(updatedProperties)
    if (controllActiveStep) controllActiveStep(activeStepIndex)
  }, [activeStepIndex])

  React.useEffect(() => {
    if (controllSelectedAccount) controllSelectedAccount(account && account.accountId || DefaultPersonalAccountId)
  }, [selectedAccountIndex, account])
  


  React.useEffect(() => {
    (async () => {
      if (!policy) {
        setLoadingLocal(true)
        setPolicy(await getKYCPolicy(selectedAccountIndex))
        const localInvestor = await getInvestor()
        const status = localInvestor?.accounts[selectedAccountIndex].kycStatus
        const activeIndex = status === KYCStatus.Aprroved ? 2 : status === KYCStatus.Pending ? 1 : 0
        setActiveStepIndex(activeIndex)
        setInvestor(localInvestor)
        if(loadingCompleted) loadingCompleted()
        setLoadingLocal(false)
      }
    })()
  }, [selectedAccountIndex])

  //@ts-ignore
  const emptyProps: PropertyBagProps = ({ accountIndex: selectedAccountIndex })

  const handleChange = (item: string) => (input: string) => {
    const tmp = updatedProperties || emptyProps
    tmp[item] = input
    setUpdatedProperties(tmp)
  }

  const handleSubmit = async () => {
    setLoadingLocal(true)
    setMessage(undefined)
    if (updatedProperties) {
      const updatedPropertiesLocal = updatedProperties
      updatedPropertiesLocal.accountIndex = selectedAccountIndex
      const investor = await updateInvestorKYCInformation(updatedPropertiesLocal)
      if (investor) setInvestor(investor)
      if (investor?.accounts[selectedAccountIndex].kycStatus === KYCStatus.Incomplete) setMessage(lang(TranslatedTerms.KYC_INCOMPLETE_MESSAGE))
      if (investor?.accounts[selectedAccountIndex].kycStatus === KYCStatus.Pending) {
        setMessage(lang(TranslatedTerms.KYC_PENDING_MESSAGE))
        setUpdatedProperties(undefined)
        setActiveStepIndex(1)
      }
    }
    setLoadingLocal(false)
  }

  const handleCancel = () => window.location.reload()

  if (!policy || !policy.properties) return <></>
  const nonFileItems = policy.properties.filter((item) => item.type !== FieldTypes.FileField)
  const fileItems = policy.properties.filter((item) => item.type === FieldTypes.FileField)

  const accountSelectControler = async (val: string) => {
    const tmpKey = accounts.indexOf(val)
    const key = tmpKey > 0 ? tmpKey : 0
    if (key === accounts.length - 1) return setShowCreateAccount(true)
    setShowCreateAccount(false)
    setLoadingLocal(true)
    setPolicy(await getKYCPolicy(key))
    setSelectedAccountIndex(key)
    const status = investor?.accounts[key].kycStatus
    const activeIndex = status === KYCStatus.Aprroved ? 2 : status === KYCStatus.Pending ? 1 : 0
    setActiveStepIndex(activeIndex)
    setLoadingLocal(false)
  }

  const accountExecute = (task: () => Promise<Investor | undefined>) => async () => {
    if (loading) return
    setLoadingLocal(true)
    const result = await task()
    if (result) setInvestor(result)
    const newIndex = accounts.length - 1
    setShowCreateAccount(false)
    setPolicy(await getKYCPolicy(newIndex))
    setSelectedAccountIndex(newIndex)
    setActiveStepIndex(0)
    setLoadingLocal(false)
  }

  const DefaultGridStyles = { display: 'grid', gridTemplateColumns: IsMobile(width) ? "1fr" : "repeat(2, 1fr)", gridColumnGap: 20 }

  return (
    <div>
      {message && <PopupMessage message={message} />}
      <div style={{ ...DefaultGridStyles }}>
        <div>
          <h4>{showPageInformation && lang(TranslatedTerms.HOW_DOES_IT_WORK)}</h4>
          <p className="light small">{showPageInformation && lang(TranslatedTerms.PROFILE_PAGE_DESCRIPTION)} </p>
          {!showCreateAccount && <Stepper steps={[lang(TranslatedTerms.FILL_DATA), lang(TranslatedTerms.AWAIT_APPROVAL), lang(TranslatedTerms.APPROVED)]} activeStepIndex={activeStepIndex} />}
        </div>
        <SelectInput hidePlaceholder={true}
          options={accounts} fieldName={TranslatedTerms.SELECT_ACCOUNT}
          itemRender={(e: string) => lang(e)} value={accounts[selectedAccountIndex]}
          getValue={(e: string) => e} onSelect={(id: string) => accountSelectControler(id)} />
      </div>
      {showCreateAccount && <CreateAccountInputs asyncTask={accountExecute} loading={loading} />}
      {!showCreateAccount && (
        <>
          <div style={DefaultGridStyles}>
            {nonFileItems.map((item, key) => <DynamicPropertyInput key={key} options={item.options} subTitle={item.propertyDescription}
              setValue={handleChange(item.property)} loading={loading} fieldName={item.property} type={item.type}
              value={investor && account && account.kycData[item.property] && account.kycData[item.property].content} />)}
          </div>
          <div style={DefaultGridStyles}>
            {fileItems.map((item, key) => <DynamicPropertyInput key={key} subTitle={item.propertyDescription}
              setValue={handleChange(item.property)} loading={loading} fieldName={item.property} type={item.type}
              value={investor && account && account.kycData[item.property] && account.kycData[item.property].content} />)}
          </div>
          {updatedProperties && (
            <div style={DefaultGridStyles}>
              <button style={{ marginTop: 26 }} className="light" onClick={handleCancel}>{lang(TranslatedTerms.CANCEL)}</button>
              <button style={{ marginTop: 26 }} onClick={handleSubmit}>{lang(TranslatedTerms.SEND_FOR_APPROVAL)}</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

